import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useNotifications } from '../context/NotificationContext';
import '../components/Common/Common.css';

function NewsletterSubscribers() {
  const { markAsViewed, newItems } = useNotifications();
  const [subscribers, setSubscribers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [flashingItems, setFlashingItems] = useState([]);

  useEffect(() => {
    fetchSubscribers();
    fetchStatistics();
  }, [statusFilter, searchQuery, currentPage]);

  // Mark all newsletter subscribers as viewed when page loads
  useEffect(() => {
    markAsViewed('newsletter');
  }, []);

  // Flash new items
  useEffect(() => {
    if (newItems.newsletter && newItems.newsletter.length > 0) {
      const newIds = newItems.newsletter.map(item => item.id);
      setFlashingItems(newIds);

      const timer = setTimeout(() => {
        setFlashingItems([]);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [newItems.newsletter]);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        per_page: 50,
      };

      if (statusFilter) {
        params.status = statusFilter;
      }

      if (searchQuery) {
        params.search = searchQuery;
      }

      const response = await api.get('/newsletter/subscribers', { params });
      setSubscribers(response.data.data || []);
      setTotalPages(response.data.last_page || 1);
    } catch (error) {
      console.error('Failed to fetch subscribers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/newsletter/statistics');
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const handleDeleteClick = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSubscriber) return;

    try {
      await api.delete(`/newsletter/subscribers/${selectedSubscriber.id}`);
      setShowDeleteModal(false);
      setSelectedSubscriber(null);
      fetchSubscribers();
      fetchStatistics();
      alert('Abonné supprimé avec succès');
    } catch (error) {
      console.error('Failed to delete subscriber:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (statusFilter) {
        params.status = statusFilter;
      }

      const response = await api.get('/newsletter/export', {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to export subscribers:', error);
      alert('Erreur lors de l\'exportation');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: '#10b981', bg: '#d1fae5', text: 'Actif' },
      unsubscribed: { color: '#ef4444', bg: '#fee2e2', text: 'Désabonné' },
    };

    const badge = badges[status] || badges.active;

    return (
      <span
        style={{
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: '600',
          color: badge.color,
          backgroundColor: badge.bg,
        }}
      >
        {badge.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && subscribers.length === 0) {
    return <div className="loading">Chargement des abonnés...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1>Newsletter - Abonnés</h1>
      </div>

      {/* Statistics */}
      {statistics && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <div className="stat-card" style={{ background: '#3b82f6', color: 'white' }}>
            <h3>{statistics.total_subscribers}</h3>
            <p>Total Abonnés</p>
          </div>
          <div className="stat-card" style={{ background: '#10b981', color: 'white' }}>
            <h3>{statistics.active_subscribers}</h3>
            <p>Abonnés Actifs</p>
          </div>
          <div className="stat-card" style={{ background: '#ef4444', color: 'white' }}>
            <h3>{statistics.unsubscribed_count}</h3>
            <p>Désabonnés</p>
          </div>
          <div className="stat-card" style={{ background: '#8b5cf6', color: 'white' }}>
            <h3>{statistics.recent_subscribers_30_days}</h3>
            <p>Nouveaux (30j)</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-body">
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0, flex: '1', minWidth: '250px' }}>
              <input
                type="text"
                placeholder="Rechercher par email ou nom..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Tous</option>
                <option value="active">Actifs</option>
                <option value="unsubscribed">Désabonnés</option>
              </select>
            </div>
            <button
              onClick={handleExport}
              className="btn btn-success"
              style={{ marginBottom: 0 }}
            >
              📥 Exporter CSV
            </button>
          </div>
        </div>
      </div>

      {/* Subscribers Table */}
      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Nom</th>
                <th>Statut</th>
                <th>Date d'inscription</th>
                <th>Date de désinscription</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                    Aucun abonné trouvé
                  </td>
                </tr>
              ) : (
                subscribers.map((subscriber) => (
                  <tr
                    key={subscriber.id}
                    className={flashingItems.includes(subscriber.id) ? 'flash-row' : ''}
                  >
                    <td>{subscriber.email}</td>
                    <td>{subscriber.name || '—'}</td>
                    <td>{getStatusBadge(subscriber.status)}</td>
                    <td>{formatDate(subscriber.subscribed_at)}</td>
                    <td>{formatDate(subscriber.unsubscribed_at)}</td>
                    <td>
                      <button
                        onClick={() => handleDeleteClick(subscriber)}
                        className="btn btn-sm btn-danger"
                        style={{ padding: '6px 12px', fontSize: '13px' }}
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer" style={{ display: 'flex', justifyContent: 'center', gap: '10px', padding: '15px' }}>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary"
            >
              Précédent
            </button>
            <span style={{ padding: '8px 16px' }}>
              Page {currentPage} sur {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="btn btn-secondary"
            >
              Suivant
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedSubscriber && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            className="card"
            style={{ maxWidth: '500px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header">
              <h2>Confirmer la suppression</h2>
            </div>
            <div className="card-body">
              <p>
                Êtes-vous sûr de vouloir supprimer l'abonné{' '}
                <strong>{selectedSubscriber.email}</strong> ?
              </p>
              <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '10px' }}>
                Cette action est irréversible.
              </p>
            </div>
            <div className="card-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setShowDeleteModal(false)} className="btn btn-secondary">
                Annuler
              </button>
              <button onClick={handleDeleteConfirm} className="btn btn-danger">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NewsletterSubscribers;
